import { useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import SectionName from '~/constants/new-cars/SectionName';
import { useRemoveHeader } from '~/context/common/StaticContext';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import getRestorationTarget from '~/context/new-cars/utilities/getRestorationTarget';
import { readNewCarsHistoryState } from '~/utilities/new-cars/newCarsHistoryState';

const FALLBACK_BUFFER_MS = 50;
const MFE_HEADER_TRANSITION_DURATION_MS = 150;
const TRANSITION_DURATION_MS = 300;

// Fixed step order for direction decisions. sectionSequence is dynamic and can
// briefly drop a section (e.g. ModelVersion while modelVersions reloads after a
// year change), making indexOf return -1 and flipping a forward move into a
// backward animation. ModelVersion and Doors are mutually exclusive so they share
// a rank.
const SECTION_ORDER: Record<SectionName, number> = {
  [SectionName.Year]: 0,
  [SectionName.ModelVersion]: 1,
  [SectionName.Doors]: 1,
  [SectionName.Trim]: 2,
  [SectionName.TruckBodyStyle]: 3,
  [SectionName.Options]: 4
};

type SectionRefs = Record<SectionName, React.RefObject<HTMLDivElement | null>>;

type TransitionCleanup = {
  finalizers: Array<() => void>;
};

type TransitionAnimateArgs = {
  activeSection: SectionName;
  cleanup: TransitionCleanup;
  firstSection: SectionName;
  heroRef: React.RefObject<HTMLDivElement | null>;
  mfeHeader: HTMLElement | null;
  prev: SectionName;
  sectionRefs: SectionRefs;
};

type InstantAnimateArgs = {
  activeSection: SectionName;
  firstSection: SectionName;
  forceHideHeader?: boolean;
  heroRef: React.RefObject<HTMLDivElement | null>;
  mfeHeader: HTMLElement | null;
  sectionRefs: SectionRefs;
  sectionSequence: SectionName[];
};

// Runs onDone once — via transitionend, fallback timer, or explicit force-complete.
// Idempotency means the effect cleanup can call finalize() to settle the DOM
// before starting the next animation without double-firing the callback.
function listenForTransitionEnd(
  el: HTMLElement,
  onDone: () => void,
  cleanup: TransitionCleanup,
  fallbackMs: number
) {
  let settled = false;
  let timer: ReturnType<typeof setTimeout>;
  let handler: (e: TransitionEvent) => void;

  const finalize = () => {
    if (settled) return;
    settled = true;
    el.removeEventListener('transitionend', handler);
    clearTimeout(timer);
    onDone();
  };

  handler = (e: TransitionEvent) => {
    if (e.propertyName !== 'transform') return;
    finalize();
  };

  el.addEventListener('transitionend', handler);
  timer = setTimeout(finalize, fallbackMs);
  cleanup.finalizers.push(finalize);
}

function finalizeAll(cleanup: TransitionCleanup) {
  cleanup.finalizers.forEach(finalize => finalize());
}

function getMfeHeaderWrapper(): HTMLElement | null {
  return document.getElementById('mfe-header-wrapper');
}

type TransitionDuration = 'duration-150' | 'duration-300' | 'duration-500';

const ALL_DURATION_CLASSES: TransitionDuration[] = [
  'duration-150',
  'duration-300',
  'duration-500'
];

function setTranslate(el: HTMLElement, cls: string) {
  el.classList.remove('-translate-y-full', 'translate-y-0', 'translate-y-full');
  el.classList.add(cls);
}

function showSection(el: HTMLElement) {
  el.style.transform = '';
  el.classList.remove(
    'hidden',
    '-translate-y-full',
    'translate-y-full',
    'transition-transform',
    'ease-in-out',
    ...ALL_DURATION_CLASSES
  );
  el.classList.add('translate-y-0');
}

function hideSection(el: HTMLElement) {
  el.style.transform = '';
  el.classList.remove(
    '-translate-y-full',
    'translate-y-0',
    'translate-y-full',
    'transition-transform',
    'ease-in-out',
    ...ALL_DURATION_CLASSES
  );
  el.classList.add('hidden');
}

function enableTransition(
  el: HTMLElement,
  duration: TransitionDuration = 'duration-300'
) {
  el.classList.remove(...ALL_DURATION_CLASSES);
  el.classList.add('transition-transform', duration, 'ease-in-out');
}

function disableTransition(el: HTMLElement) {
  el.classList.remove(
    'transition-transform',
    'ease-in-out',
    ...ALL_DURATION_CLASSES
  );
}

function matchBodyBackground() {
  document.body.style.backgroundColor = 'var(--color-background-weak)';
}

function restoreBodyBackground() {
  document.body.style.backgroundColor = '';
}

function mfeHeaderSlideUp(el: HTMLElement, cleanup: TransitionCleanup) {
  // z-index keeps the MFE header above the sliding content.
  matchBodyBackground();
  el.classList.add('relative', 'z-10');
  enableTransition(el, 'duration-150');
  setTranslate(el, '-translate-y-full');

  listenForTransitionEnd(
    el,
    () => hideSection(el),
    cleanup,
    MFE_HEADER_TRANSITION_DURATION_MS + FALLBACK_BUFFER_MS
  );
}

function mfeHeaderSlideDown(el: HTMLElement) {
  restoreBodyBackground();
  disableTransition(el);
  el.classList.remove('hidden');
  el.classList.add('relative', 'z-10');
  setTranslate(el, '-translate-y-full');
  void el.offsetHeight; // force reflow so the browser registers the start position

  enableTransition(el);
  setTranslate(el, 'translate-y-0');
}

function mfeHeaderShowInstantly(el: HTMLElement) {
  restoreBodyBackground();
  showSection(el);
  el.classList.remove('relative', 'z-10');
}

function mfeHeaderHideInstantly(el: HTMLElement) {
  matchBodyBackground();
  hideSection(el);
  el.classList.remove('relative', 'z-10');
}

function animateForward({
  activeSection,
  cleanup,
  firstSection,
  heroRef,
  mfeHeader,
  prev,
  sectionRefs
}: TransitionAnimateArgs) {
  const leavingEl = sectionRefs[prev].current;
  const enteringEl = sectionRefs[activeSection].current;

  if (leavingEl) {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const heroHeight = heroRef.current?.offsetHeight ?? 0;
    const distance = Math.max(
      leavingEl.offsetHeight + heroHeight,
      window.innerHeight
    );
    enableTransition(leavingEl);
    leavingEl.style.transform = `translateY(-${distance}px)`;

    listenForTransitionEnd(
      leavingEl,
      () => {
        leavingEl.style.transform = '';
        hideSection(leavingEl);
        if (prev === firstSection && heroRef.current) {
          heroRef.current.style.transform = '';
          hideSection(heroRef.current);
        }

        if (enteringEl) {
          enteringEl.style.transform = '';
          disableTransition(enteringEl);
          enteringEl.classList.remove('hidden');
          setTranslate(enteringEl, 'translate-y-full');
          void enteringEl.offsetHeight; // force reflow so the browser registers the start position

          enableTransition(enteringEl);
          setTranslate(enteringEl, 'translate-y-0');
        }
      },
      cleanup,
      TRANSITION_DURATION_MS + FALLBACK_BUFFER_MS
    );
  }

  if (prev === firstSection) {
    if (mfeHeader) mfeHeaderSlideUp(mfeHeader, cleanup);
    if (heroRef.current) {
      matchBodyBackground();
      enableTransition(heroRef.current);
      heroRef.current.style.transform = 'translateY(-100vh)';
    }
  }
}

function animateBackward({
  activeSection,
  cleanup,
  firstSection,
  heroRef,
  mfeHeader,
  prev,
  sectionRefs
}: TransitionAnimateArgs) {
  const leavingEl = sectionRefs[prev].current;
  const enteringEl = sectionRefs[activeSection].current;

  window.scrollTo({ top: 0, behavior: 'instant' });

  if (enteringEl) {
    enteringEl.style.transform = '';
    disableTransition(enteringEl);
    enteringEl.classList.remove('hidden');
    setTranslate(enteringEl, '-translate-y-full');
    void enteringEl.offsetHeight; // force reflow so the browser registers the start position

    enableTransition(enteringEl);
    setTranslate(enteringEl, 'translate-y-0');
  }

  if (activeSection === firstSection) {
    if (mfeHeader) mfeHeaderSlideDown(mfeHeader);
    if (heroRef.current) {
      restoreBodyBackground();
      disableTransition(heroRef.current);
      heroRef.current.classList.remove('hidden');
      heroRef.current.style.transform = 'translateY(-100vh)';
      void heroRef.current.offsetHeight;

      enableTransition(heroRef.current);
      heroRef.current.style.transform = '';
      setTranslate(heroRef.current, 'translate-y-0');
    }
  }

  if (leavingEl) {
    leavingEl.style.transform = '';
    disableTransition(leavingEl);
    setTranslate(leavingEl, 'translate-y-0');
    void leavingEl.offsetHeight;

    enableTransition(leavingEl);
    setTranslate(leavingEl, 'translate-y-full');

    listenForTransitionEnd(
      leavingEl,
      () => hideSection(leavingEl),
      cleanup,
      TRANSITION_DURATION_MS + FALLBACK_BUFFER_MS
    );
  }
}

function animateInstant({
  activeSection,
  firstSection,
  forceHideHeader = false,
  heroRef,
  mfeHeader,
  sectionRefs,
  sectionSequence
}: InstantAnimateArgs) {
  sectionSequence.forEach(name => {
    const el = sectionRefs[name].current;
    if (!el) return;
    if (name === activeSection) {
      showSection(el);
    } else {
      hideSection(el);
    }
  });

  const showHeader = activeSection === firstSection && !forceHideHeader;

  if (mfeHeader) {
    if (showHeader) {
      mfeHeaderShowInstantly(mfeHeader);
    } else {
      mfeHeaderHideInstantly(mfeHeader);
    }
  }

  if (heroRef.current) {
    if (showHeader) {
      mfeHeaderShowInstantly(heroRef.current);
    } else {
      mfeHeaderHideInstantly(heroRef.current);
    }
  }

  window.scrollTo({ top: 0, behavior: 'instant' });
}

export default function useSectionAnimation() {
  const { activeSection, sectionSequence } = useContext(ParentModelContext);
  const removeHeader = useRemoveHeader();

  const firstSection = sectionSequence[0];

  // Read via refs in the animation effect so sectionSequence/firstSection are
  // excluded from its dep array — sequence changes must not cancel in-flight animations.
  const sectionSequenceRef = useRef(sectionSequence);
  sectionSequenceRef.current = sectionSequence;
  const firstSectionRef = useRef(firstSection);
  firstSectionRef.current = firstSection;

  const isRestoringRef = useRef(false);
  const restorationTargetRef = useRef<SectionName>(firstSection);
  const heroRef = useRef<HTMLDivElement>(null);
  const prevSection = useRef<SectionName>(firstSection);
  const mountActiveSectionRef = useRef(activeSection);
  const mountSectionSequenceRef = useRef(sectionSequence);

  const doorsRef = useRef<HTMLDivElement>(null);
  const modelVersionRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const trimRef = useRef<HTMLDivElement>(null);
  const truckBodyStyleRef = useRef<HTMLDivElement>(null);
  const yearRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useMemo<SectionRefs>(
    () => ({
      doors: doorsRef,
      modelVersion: modelVersionRef,
      options: optionsRef,
      trim: trimRef,
      truckBodyStyle: truckBodyStyleRef,
      year: yearRef
    }),
    []
  );

  // Snaps the DOM to the correct section before first paint. Mount-time refs let
  // this use [] deps without lint warnings.
  useLayoutEffect(() => {
    const initialActiveSection = mountActiveSectionRef.current;
    const mfeHeader = getMfeHeaderWrapper();

    const historyState = readNewCarsHistoryState();
    const sequenceForRestoration = mountSectionSequenceRef.current;

    const target = historyState
      ? getRestorationTarget(historyState, sequenceForRestoration)
      : undefined;

    const effectiveSection = target ?? initialActiveSection;

    animateInstant({
      activeSection: effectiveSection,
      firstSection: sequenceForRestoration[0],
      forceHideHeader: removeHeader,
      heroRef,
      mfeHeader,
      sectionRefs,
      sectionSequence: sequenceForRestoration
    });
    prevSection.current = effectiveSection;

    if (target && target !== initialActiveSection) {
      isRestoringRef.current = true;
      restorationTargetRef.current = target;

      const timeout = setTimeout(() => {
        isRestoringRef.current = false;
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [removeHeader, sectionRefs]);

  useEffect(() => {
    const prev = prevSection.current;
    const mfeHeader = getMfeHeaderWrapper();

    if (prev === activeSection) return;
    prevSection.current = activeSection;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    // Skip intermediate sections during restoration — the layout effect already
    // snapped the DOM to the target.
    if (isRestoringRef.current) {
      if (activeSection === restorationTargetRef.current) {
        isRestoringRef.current = false;
      }
      prevSection.current = activeSection;

      return;
    }

    const currentSequence = sectionSequenceRef.current;
    const currentFirstSection = firstSectionRef.current;

    if (prefersReducedMotion) {
      animateInstant({
        activeSection,
        firstSection: currentFirstSection,
        forceHideHeader: removeHeader,
        heroRef,
        mfeHeader,
        sectionRefs,
        sectionSequence: currentSequence
      });

      return;
    }

    const cleanup: TransitionCleanup = { finalizers: [] };
    const direction =
      SECTION_ORDER[activeSection] > SECTION_ORDER[prev]
        ? 'forward'
        : 'backward';
    const args = {
      activeSection,
      cleanup,
      firstSection: currentFirstSection,
      heroRef,
      mfeHeader,
      prev,
      sectionRefs
    };

    if (direction === 'forward') {
      animateForward(args);
    } else {
      animateBackward(args);
    }

    // Force-complete the in-flight animation so the DOM is fully settled before
    // the next one starts. sectionSequence/firstSection excluded from deps — see refs above.
    return () => finalizeAll(cleanup);
  }, [activeSection, removeHeader, sectionRefs]);

  useEffect(() => restoreBodyBackground, []);

  return {
    activeSection,
    heroRef,
    sectionRefs
  };
}
